import { ClientContractEntity, ClientCustomerEntity, IInternalAuthentication } from '@edgebox/api-rest-client';
import { CustomerRole, InternalRole, UserType } from '@edgebox/data-definitions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-light-svg-icons/faCirclePlus';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faSidebar } from '@fortawesome/pro-light-svg-icons/faSidebar';
import React from 'react';
import { Dropdown, Navbar } from '@edgebox/react-components';
import { Link } from 'react-router-dom';
import {
  ApiComponent,
  AuthenticationContext,
  IApiComponentState,
  Logo,
  SyncCoreDataDefinitionsEnumValue,
  ViewContractName,
  WithMostRecentContractRevision,
} from '../common';
import { IAppContextProp, withAppContext } from '../common/contexts/AppContext';
import { HostingType, Product, SiteApplicationType, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { EnvironmentBadge } from './EnvironmentBadge';
import { ICONS, SiteApplicationIcon } from './Icons';
import { ILocationProp, INavigateProp, withLocation, withNavigate } from './RouterHelper';
import { HumanDrawnDash } from './Shared/HumanDrawnDash';
import { TopNavEntitySelector } from './Shared/TopNavEntitySelector';

interface IProps extends ILocationProp, INavigateProp, IAppContextProp, React.JSX.IntrinsicAttributes {
  /** Opens the page's side menu, which a narrow screen keeps closed; set only on pages that have one. */
  onOpenSideMenu?: () => void;
}

interface IState extends IApiComponentState {
  menuExpanded?: boolean;
}

type MainNavKey = 'governance' | 'product' | 'sync-cores' | 'admin';

/** Whether the path shows the Brand Presence area, whose screens the frame from Embed renders. */
export function isGovernancePath(pathname: string): boolean {
  return !!pathname.match(/^\/projects\/[^/]+\/brand-presence(\/|$)/);
}

/**
 * Check whether the current path is for Syndication / Content Cloud or not.
 */
export function isProductPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    !!pathname.match(/^\/(onboarding|content|updates|sites|sync-cores|links|content-cloud)(\/|$)/) ||
    !!pathname.match(/^\/projects\/[^/]+\/(onboarding|dashboard|updates|content|content-cloud)(\/|$)/) ||
    !!pathname.match(/^\/projects\/[^/]+\/(sites|sync-cores)\/[^/]+(\/|$)/)
  );
}

function getProductModeLabel(appType?: SiteApplicationType): string {
  return appType === SiteApplicationType.ContentCloud
    ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('SiteApplicationType', SiteApplicationType.ContentCloud)
    : SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('Product', Product.Syndication);
}

/** The hand-drawn underline of the active area, placed relative to the navigation container. */
interface IMainNavIndicator {
  left: number;
  top: number;
  width: number;
  visible: boolean;
}

const MAIN_NAV_INDICATOR_HEIGHT_PX = 10;

interface IMainNavigationProps {
  pathname: string;
  governanceTarget: string;
  productTarget: string;
  productLabel: string;
  showGovernance: boolean;
  showSyncCores: boolean;
  showAdmin: boolean;
}

function MainNavigation({
  pathname,
  governanceTarget,
  productTarget,
  productLabel,
  showGovernance,
  showSyncCores,
  showAdmin,
}: IMainNavigationProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const animationFrameRef = React.useRef<number | undefined>(undefined);
  const currentIndicatorRef = React.useRef<IMainNavIndicator>({ left: 0, top: 0, width: 0, visible: false });
  const [hoveredKey, setHoveredKey] = React.useState<MainNavKey | undefined>(undefined);
  const [indicator, setIndicator] = React.useState<IMainNavIndicator>({
    left: 0,
    top: 0,
    width: 0,
    visible: false,
  });

  const animationDurationMs = 250;

  const isGovernance = isGovernancePath(pathname);
  const isProduct = isProductPath(pathname);
  const isAdmin = !!pathname.match(/^\/backend(\/|$)/);
  const isSyncCores = !!pathname.match(/^\/sync-cores(\/|$)/);

  const getMainNavLinkClassName = (key: MainNavKey, isActive: boolean) => {
    const isHighlighted = isActive || hoveredKey === key;
    return `text-decoration-none text-nowrap px-3 py-1 d-inline-flex align-items-center ${isHighlighted ? 'text-black' : 'text-dark'} ${isActive ? 'fw-semibold' : ''}`;
  };

  const getMainNavLinkProps = (key: MainNavKey, isActive: boolean) => {
    return {
      onMouseEnter: () => setHoveredKey(key),
      onMouseLeave: () => setHoveredKey((prev) => (prev === key ? undefined : prev)),
      className: getMainNavLinkClassName(key, isActive),
      style: { transition: 'color 0.25s ease' } as React.CSSProperties,
    };
  };

  const getCurrentMainNavKey = React.useCallback((): MainNavKey | undefined => {
    if (isAdmin) {
      return 'admin';
    }
    if (isSyncCores) {
      return 'sync-cores';
    }
    if (isGovernance) {
      return 'governance';
    }
    if (isProduct) {
      return 'product';
    }

    return undefined;
  }, [isAdmin, isSyncCores, isGovernance, isProduct]);

  const onGovernanceClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // The entry points at the area's entry path, which Embed only acts on while it sits at the
      // area root. From anywhere inside the area the screen therefore cannot move, and the click
      // costs a history entry that has lost the screen's query string. A modified click, which
      // opens the area in its own tab, is left to the browser.
      const opensElsewhere = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (isGovernance && !opensElsewhere) {
        event.preventDefault();
      }
    },
    [isGovernance]
  );

  const updateIndicator = React.useCallback(() => {
    const activeKey = getCurrentMainNavKey();
    const container = containerRef.current;
    const activeItem = activeKey ? (container?.querySelector(`[data-main-nav-key="${activeKey}"]`) as HTMLAnchorElement | null) : undefined;

    const containerRect = container?.getBoundingClientRect();
    // A collapsed menu lays the container out at zero size; the underline waits until it is shown.
    if (!container || !activeItem || !containerRect?.width) {
      currentIndicatorRef.current = { ...currentIndicatorRef.current, visible: false };
      setIndicator((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    const activeRect = activeItem.getBoundingClientRect();
    const containerPaddingBottom = parseFloat(window.getComputedStyle(container).paddingBottom) || 0;
    const nextLeft = activeRect.left - containerRect.left;
    // The underline ends at the container's bottom padding below the active entry, so it follows the
    // entry whether the entries sit in one row or are stacked in the collapsed menu.
    const nextTop = activeRect.bottom - containerRect.top + containerPaddingBottom - MAIN_NAV_INDICATOR_HEIGHT_PX;
    const nextWidth = activeRect.width;

    const startIndicator = currentIndicatorRef.current;
    if (
      startIndicator.visible === true &&
      startIndicator.left === nextLeft &&
      startIndicator.top === nextTop &&
      startIndicator.width === nextWidth
    ) {
      return;
    }

    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startLeft = startIndicator.left;
    const startTop = startIndicator.visible ? startIndicator.top : nextTop;
    const startWidth = startIndicator.width;
    const targetLeft = nextLeft;
    const targetTop = nextTop;
    const targetWidth = nextWidth;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(1, elapsed / animationDurationMs);

      const animatedLeft = startLeft + (targetLeft - startLeft) * rawProgress;
      const animatedTop = startTop + (targetTop - startTop) * rawProgress;
      const halfwayWidth = targetWidth / 2;
      const animatedWidth =
        rawProgress <= 0.5
          ? startWidth + (halfwayWidth - startWidth) * (rawProgress / 0.5)
          : halfwayWidth + (targetWidth - halfwayWidth) * ((rawProgress - 0.5) / 0.5);

      const nextIndicatorState = {
        left: animatedLeft,
        top: animatedTop,
        width: Math.max(0, animatedWidth),
        visible: true,
      };

      currentIndicatorRef.current = nextIndicatorState;
      setIndicator(nextIndicatorState);

      if (rawProgress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        animationFrameRef.current = undefined;
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
  }, [getCurrentMainNavKey, animationDurationMs]);

  React.useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  React.useEffect(() => {
    const onResize = () => updateIndicator();
    window.addEventListener('resize', onResize);
    // Opening the collapsed menu or wrapping the bar moves the entries without resizing the window.
    const container = containerRef.current;
    const observer = container && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : undefined;
    if (container && observer) {
      observer.observe(container);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [updateIndicator]);

  React.useEffect(() => {
    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      className="position-relative d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 ms-lg-2 pb-1"
      ref={containerRef}
    >
      {showGovernance ? (
        <Link
          data-main-nav-key="governance"
          to={governanceTarget}
          {...getMainNavLinkProps('governance', isGovernance)}
          aria-current={isGovernance ? 'true' : undefined}
          onClick={onGovernanceClick}
        >
          <SyncCoreDataDefinitionsEnumValue enumName={'Product'} keyName={Product.Governance} />
        </Link>
      ) : null}
      <Link data-main-nav-key="product" to={productTarget} {...getMainNavLinkProps('product', isProduct)}>
        {productLabel}
      </Link>
      {showSyncCores ? (
        <Link data-main-nav-key="sync-cores" to={'/sync-cores'} {...getMainNavLinkProps('sync-cores', isSyncCores)}>
          Sync Cores
        </Link>
      ) : null}
      {showAdmin ? (
        <Link data-main-nav-key="admin" to={'/backend'} {...getMainNavLinkProps('admin', isAdmin)}>
          Administer
        </Link>
      ) : null}
      <div
        className="position-absolute"
        style={{
          left: indicator.left,
          top: indicator.top,
          width: indicator.width,
          height: `${MAIN_NAV_INDICATOR_HEIGHT_PX}px`,
          opacity: indicator.visible ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 0.15s ease',
        }}
      >
        <HumanDrawnDash
          color="highlight"
          width="100%"
          height={`${MAIN_NAV_INDICATOR_HEIGHT_PX}`}
          style={{ display: 'block', height: `${MAIN_NAV_INDICATOR_HEIGHT_PX}px` }}
          preserveAspectRatio="none"
        />
      </div>
    </div>
  );
}

class TopNavigationComponent extends ApiComponent<IProps, IState> {
  async load() {
    return {};
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    // Following a link out of the collapsed menu closes it, so the page it opened is in view.
    if (prevProps.location.pathname !== this.props.location.pathname && this.state.menuExpanded) {
      this.setState({ menuExpanded: false });
    }
  }

  private onMenuToggle = (expanded: boolean) => {
    this.setState({ menuExpanded: expanded });
  };

  render() {
    const { appContext, location, onOpenSideMenu } = this.props;
    const { project, contract, customer } = appContext;

    const currentUser = this.api.currentUser;
    const customerRoles = currentUser?.customerRoles;
    const canManageUsers = customerRoles?.includes(CustomerRole.Owner);
    const canViewInvoices = customerRoles?.includes(CustomerRole.Owner) || customerRoles?.includes(CustomerRole.ManageFinances);
    const canManageProjects = customerRoles?.includes(CustomerRole.Owner) || customerRoles?.includes(CustomerRole.ManageSites);
    const canManageContracts = customerRoles?.includes(CustomerRole.Owner) || customerRoles?.includes(CustomerRole.ManageFinances);
    const canManageSyncCores = customerRoles?.includes(CustomerRole.Owner) || customerRoles?.includes(CustomerRole.ManageSites);
    const isInternalUser = currentUser?.type === UserType.Internal;
    const internalRoles = currentUser?.customer?.getId() === customer?.id ? currentUser?.internalRoles : undefined;
    const canAdminister = internalRoles?.includes(InternalRole.Admin) || internalRoles?.includes(InternalRole.DevOps);

    const productLabel = getProductModeLabel(project?.appType);
    const showGovernance = !!project?.enabledProducts?.includes(Product.Governance);

    const governanceTarget = project ? `/projects/${project.id}/brand-presence` : '/projects';
    const productTarget = project ? `/projects/${project.id}/onboarding` : '/projects';

    const firstName = currentUser?.firstName || currentUser?.firstName;

    const projectCacheKey = `${customer?.id || ''}-${contract?.id || ''}`;
    const ownCustomerId = currentUser?.customer?.getId();
    const isAwayFromOwnCustomer = !!ownCustomerId && customer?.id !== ownCustomerId;

    // Below the lg breakpoint the bar keeps the logo and collapses everything else into a menu the
    // toggle opens, the way the design system's header does. From lg up it is one row of three
    // columns, each centred in the bar: the logo, the selectors and the areas, and the account menu.
    // The selectors give way down to their minimum width before the row gets wider than the window,
    // and where the areas do not fit beside them they go to a second line of the middle column. A
    // selector's open menu drops below the selectors and stays inside that column, so it never lies
    // over the account menu.
    return (
      <Navbar
        expand="lg"
        bg="white"
        sticky="top"
        expanded={!!this.state.menuExpanded}
        onToggle={this.onMenuToggle}
        className="border-bottom ps-3 pe-3 pt-2 pb-2 column-gap-3"
      >
        {onOpenSideMenu ? (
          <button
            type="button"
            className="d-lg-none bg-transparent border-0 p-1 fs-4"
            aria-label="Open the side menu"
            onClick={onOpenSideMenu}
          >
            <FontAwesomeIcon icon={faSidebar} />
          </button>
        ) : null}

        <div className="d-flex flex-column align-items-center flex-shrink-0" style={{ width: '130px' }}>
          <Logo height={32} />
          <div style={{ marginTop: '-5px', height: '14px' }}>
            <EnvironmentBadge />
          </div>
        </div>

        <Navbar.Toggle aria-controls="top-nav-menu" className="ms-auto" />

        <Navbar.Collapse id="top-nav-menu" className="min-width-0">
          <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 w-100 pt-3 pt-lg-0">
            <div className="d-flex flex-column flex-lg-row flex-lg-wrap align-items-stretch align-items-lg-center gap-3 flex-lg-grow-1 min-width-0">
              <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 min-width-0">
                <div className="flex-shrink-0 border-start d-none d-lg-block" style={{ height: '30px' }} />

                {appContext.hasMultipleCustomers && (
                  <div className="top-nav-selector d-flex align-items-center">
                    <TopNavEntitySelector<ClientCustomerEntity>
                      toggleId="top-nav-customer-menu"
                      current={customer}
                      optionKey={(c) => c.id}
                      renderToggleLabel={(c) => <span className="text-truncate">{c?.name || 'Customer'}</span>}
                      loadOptions={async (query) => {
                        const result = await this.api.billing.customers.findByName(query, { page: 0, itemsPerPage: 100 });
                        return {
                          items: [...result.items].sort((a, b) => a.name.localeCompare(b.name)),
                          total: result.totalNumberOfItems,
                        };
                      }}
                      entityNamePlural="customers"
                      renderOption={(c) => <span>{c.name}</span>}
                      onSelect={(selected) => {
                        void appContext.setCustomer?.(selected);
                      }}
                      onReset={
                        isAwayFromOwnCustomer
                          ? () => {
                              void (async () => {
                                const own = await this.getCurrentCustomer();
                                if (own) {
                                  await appContext.setCustomer?.(own);
                                }
                              })();
                            }
                          : undefined
                      }
                      emptyLabel="No customers available"
                    />
                  </div>
                )}

                {appContext.hasMultipleContracts && contract && (
                  <div className="top-nav-selector d-flex align-items-center">
                    <TopNavEntitySelector<ClientContractEntity>
                      toggleId="top-nav-contract-menu"
                      current={contract}
                      cacheKey={customer?.id || ''}
                      optionKey={(c) => c.id}
                      renderToggleLabel={(c) => (
                        <span className="text-truncate">{c ? <ViewContractName entity={c} includeDate={false} /> : 'Contract'}</span>
                      )}
                      headerActions={
                        canManageContracts && contract.customer.getId() === ownCustomerId ? (
                          <Dropdown.Item as={Link} className="px-3 py-2" to={`/subscriptions/${contract.id}`}>
                            <FontAwesomeIcon icon={faGear} className="me-2" />
                            Manage subscription
                          </Dropdown.Item>
                        ) : undefined
                      }
                      loadOptions={async (query) => {
                        const result = await this.api.billing.contracts.search(
                          query || undefined,
                          { page: 0, itemsPerPage: 100 },
                          customer?.id,
                          false
                        );
                        return {
                          items: [...result.items].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
                          total: result.totalNumberOfItems,
                        };
                      }}
                      entityNamePlural="contracts"
                      renderOption={(c) => (
                        <span className="d-inline-block mw-100 text-truncate">
                          <ViewContractName entity={c} />
                        </span>
                      )}
                      onSelect={(selected) => {
                        void appContext.setContract?.(selected, customer);
                      }}
                      emptyLabel="No contracts available"
                    />
                  </div>
                )}

                {/* The last selector's menu lines up with its right edge from lg up, so it stays above
                  the middle column and stops short of the account menu beside it. */}
                <div className="top-nav-selector d-flex align-items-center">
                  <TopNavEntitySelector
                    toggleId="top-nav-project-menu"
                    menuAlign={{ lg: 'end' }}
                    current={project}
                    cacheKey={projectCacheKey}
                    optionKey={(p) => p.id}
                    renderToggleLabel={(p) => (
                      <>
                        <span className="d-inline-flex justify-content-center me-2" style={{ width: '18px' }}>
                          <SiteApplicationIcon type={p?.appType || SiteApplicationType.Drupal} />
                        </span>
                        <span className="text-truncate">{p?.name || 'Project'}</span>
                      </>
                    )}
                    headerActions={
                      <>
                        <Dropdown.Item
                          as={Link}
                          className="px-3 py-2"
                          to={project ? `/projects/${project.id}/settings/overview` : '/projects'}
                        >
                          <FontAwesomeIcon icon={faGear} className="me-2" />
                          Settings
                        </Dropdown.Item>
                        {canManageProjects && (
                          <Dropdown.Item as={Link} className="px-3 py-2" to="/projects/add">
                            <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
                            Add Project
                          </Dropdown.Item>
                        )}
                      </>
                    }
                    loadOptions={async (query) => {
                      const result = await this.api.syndication.projects.search(
                        { customerId: customer?.id, contractId: contract?.id },
                        { page: 0, itemsPerPage: 100 },
                        { includeInactive: true, search: query || undefined }
                      );
                      return {
                        items: [...result.items].sort((a, b) => a.name.localeCompare(b.name)),
                        total: result.totalNumberOfItems,
                      };
                    }}
                    entityNamePlural="projects"
                    renderOption={(p) => (
                      <span className="d-grid align-items-center" style={{ gridTemplateColumns: '20px 1fr', columnGap: '8px' }}>
                        <span className="d-inline-flex justify-content-center">
                          <SiteApplicationIcon type={p.appType || SiteApplicationType.Drupal} />
                        </span>
                        <span>{p.name}</span>
                      </span>
                    )}
                    getOptionHref={(p) =>
                      `/projects/${p.id}/${!p.appType || p.appType === SiteApplicationType.Drupal ? 'dashboard' : 'settings/overview'}`
                    }
                    onSelect={() => {}}
                    emptyLabel="No projects available"
                  />
                </div>

                <div className="flex-shrink-0 border-start d-none d-lg-block" style={{ height: '30px' }} />
              </div>

              <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 flex-lg-grow-1">
                <WithMostRecentContractRevision contractId={contract?.id}>
                  {(revision) => {
                    const isOnPremise = revision.current?.hostingType === HostingType.OnPremise;
                    return (
                      <MainNavigation
                        pathname={location.pathname}
                        governanceTarget={governanceTarget}
                        productTarget={productTarget}
                        productLabel={productLabel}
                        showGovernance={showGovernance}
                        showSyncCores={!!canManageSyncCores && (isInternalUser || isOnPremise)}
                        showAdmin={!!canAdminister}
                      />
                    );
                  }}
                </WithMostRecentContractRevision>
              </div>
            </div>

            <div className="flex-shrink-0">
              <Dropdown align={{ lg: 'end' }}>
                <Dropdown.Toggle
                  variant=""
                  id="top-nav-user-menu"
                  className="text-start ps-3 pe-3 pt-2 pb-2 border rounded-pill shadow-sm d-inline-flex align-items-center mw-100"
                >
                  <FontAwesomeIcon icon={ICONS.User.light} className="me-2 mt-1 mb-1" />
                  <span className="top-nav-account-name d-inline-block text-truncate min-width-0">{firstName || 'Account'}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-sm">
                  <Dropdown.Item
                    as={Link}
                    to="/account/profile"
                    className={`p-2 ${location.pathname === '/account/profile' ? 'text-primary' : ''}`}
                  >
                    <span className="d-inline-block text-center me-1" style={{ width: '32px' }}>
                      <FontAwesomeIcon icon={ICONS.User[location.pathname === '/account/profile' ? 'regular' : 'light']} />
                    </span>
                    My account
                  </Dropdown.Item>
                  {canViewInvoices || canManageUsers ? <Dropdown.Divider /> : null}
                  {customer && canViewInvoices && (
                    <Dropdown.Item
                      as={Link}
                      to="/subscriptions"
                      className={`p-2 ${location.pathname.startsWith('/subscriptions') ? 'text-primary' : ''}`}
                    >
                      <span className="d-inline-block text-center me-1" style={{ width: '32px' }}>
                        <FontAwesomeIcon icon={ICONS.Contract[location.pathname.startsWith('/subscriptions') ? 'regular' : 'light']} />
                      </span>
                      Subscriptions
                    </Dropdown.Item>
                  )}
                  {canManageUsers && (
                    <Dropdown.Item
                      as={Link}
                      to="/account/users"
                      className={`p-2 ${location.pathname === '/account/users' ? 'text-primary' : ''}`}
                    >
                      <span className="d-inline-block text-center me-1" style={{ width: '32px' }}>
                        <FontAwesomeIcon icon={ICONS.Users[location.pathname === '/account/users' ? 'regular' : 'light']} />
                      </span>
                      Users
                    </Dropdown.Item>
                  )}
                  {customer && canViewInvoices && (
                    <Dropdown.Item
                      as={Link}
                      to="/account/billing"
                      className={`p-2 ${location.pathname === '/account/billing' ? 'text-primary' : ''}`}
                    >
                      <span className="d-inline-block text-center me-1" style={{ width: '32px' }}>
                        <FontAwesomeIcon icon={ICONS.Invoice[location.pathname === '/account/billing' ? 'regular' : 'light']} />
                      </span>
                      Billing
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <AuthenticationContext.Consumer>
                    {(auth: IInternalAuthentication) => (
                      <Dropdown.Item onClick={() => auth.logout()} className={'p-2'}>
                        <span className="d-inline-block text-center me-1" style={{ width: '32px' }}>
                          <FontAwesomeIcon icon={ICONS.Logout['light']} />
                        </span>
                        Logout
                      </Dropdown.Item>
                    )}
                  </AuthenticationContext.Consumer>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Navbar.Collapse>
      </Navbar>
    );
  }
}

export const TopNavigation = withNavigate<Omit<IProps, 'appContext' | 'location' | 'navigate'>>(
  withLocation<Omit<IProps, 'appContext' | 'location'>>(withAppContext(TopNavigationComponent))
);
